import { StandingAccessReportAppModelStatus } from '@api';
import { Dot, MaterialIcon } from '@components';
import { CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { AssessmentReportStatus } from '@StandingAccess/types';

type StatusConfig = {
  label: string;
  color: 'warning' | 'success' | 'error' | 'secondary';
  icon: (color: StatusConfig['color']) => React.ReactNode;
};

interface AssessmentStatusProps {
  reportStatus: StandingAccessReportAppModelStatus;
}

const STATUS_CONFIGS: Record<AssessmentReportStatus, StatusConfig | undefined> = {
  [AssessmentReportStatus.InProgress]: {
    label: 'Assessment in progress',
    color: 'warning',
    icon: (color) => <CircularProgress size={16} color={color} />,
  },
  [AssessmentReportStatus.Ready]: {
    label: 'Ready',
    color: 'success',
    icon: (color) => <Dot color={color} size="small" />,
  },
  [AssessmentReportStatus.ReAssess]: {
    label: 'Assessment in progress',
    color: 'warning',
    icon: (color) => <CircularProgress size={16} color={color} />,
  },
  [AssessmentReportStatus.Error]: {
    label: 'Error',
    color: 'error',
    icon: () => <MaterialIcon symbol="warning" color="error" weight={700} block />,
  },
  [AssessmentReportStatus.Warning]: {
    label: 'Warning',
    color: 'warning',
    icon: () => <MaterialIcon symbol="warning" color="warning" weight={700} block />,
  },
};

function StatusIcon({ status, message }: { status: AssessmentReportStatus; message?: string }) {
  const config = STATUS_CONFIGS[status];
  const icon = config?.icon(config?.color);

  if (!message) {
    return <>{icon}</>;
  }

  return (
    <Tooltip title={message} placement="top" arrow>
      <div>{icon}</div>
    </Tooltip>
  );
}

export default function AssessmentStatus({ reportStatus }: AssessmentStatusProps) {
  const status = reportStatus.status as AssessmentReportStatus;
  const config = STATUS_CONFIGS[status];

  return (
    <Stack direction="row" alignItems="center" spacing={1} component="div" role="status">
      <StatusIcon status={status} message={reportStatus.message} />
      <Typography variant="body2" noWrap>
        {config?.label}
      </Typography>
    </Stack>
  );
}
